import axios from "axios";
import { queryParams } from "./queryParams";
import noop from "lodash/noop";
import omit from "lodash/omit";

export default class PollingRequest {
  constructor(options) {
    const query = options.query || {};

    this.options = options;
    this.options.query = JSON.parse(JSON.stringify(query));
    this.options.interval = options.interval || 1000;
    this.options.success = options.success || noop;
    this.options.progress = options.progress || noop;
    this.options.error = options.error || noop;
    this.data = [];
    this.started = false;
    this.initRequest = false;
    this.currentPage = 0;
    this.totalPages = 0;
    this.numOfRequests = 10;
    this.finalRequestUrl = null;
    this.doFinalRequest = false;
    this.headerInfo = {
      title: "",
      value: 0
    };
  }

  start(cb = noop) {
    if (!this.started) {
      this.started = true;
      cb();
    }

    if (!this.timer) {
      this.timer = setTimeout(() => {
        this.run();
      }, this.interval);
    }
  }

  clearTimer() {
    clearTimeout(this.timer);
    this.timer = undefined;
  }

  handleProgress(response) {
    const { pagination, tableInfo } = response;

    if (this.initRequest) {
      this.currentPage = this.currentPage + response.length;
      this.nextLinks = this.nextLinks.slice(this.numOfRequests);

      response.map((item) => {
        this.data = this.data.concat(item.data.tableInfo.data);
      });
      this.options.progress({
        current: this.currentPage,
        total: this.totalPages
      });

      if (this.currentPage >= this.totalPages) {
        this.clearTimer();
        return false;
      } else if (this.currentPage === this.totalPages - 1) {
        this.doFinalRequest = true;
        this.clearTimer();
        this.start();
        return true;
      }
      this.clearTimer();
      this.start();
      return true;
    }

    if (pagination && pagination.page === 1 && pagination.nextPageLinks) {
      this.finalRequestUrl = pagination.nextPageLinks.slice(-1).pop();
      this.initRequest = true;
      this.totalPages = pagination.pages;
      this.currentPage = 1;
    }

    if (pagination && pagination.nextPageLinks) {
      this.nextLinks = pagination.nextPageLinks.slice(0, -1);
      this.data = this.data.concat(tableInfo.data);

      this.options.progress({
        current: pagination.page,
        total: pagination.pages
      });

      this.clearTimer();
      this.start();
      return true;
    } else {
      this.data = this.data.concat(tableInfo.data);
      this.clearTimer();
      return false;
    }
  }

  handleFinished(data) {
    this.options.success(data);
    this.clearTimer();
  }

  handleFail(errors) {
    this.options.error(errors);
    this.clearTimer();
  }

  async run() {
    let rawDatapath;
    let rawParams = this.getShortParams();
    const { query } = this.options;
    const baseUrl = `${queryParams.apiVersion}${query.data_url}`;
    const shortUrl = query.short_url;

    if (this.initRequest) {
      let requests = [];
      this.nextLinks.map((item, index) => {
        // We don't need to send params for the next links in pagination
        // rawParams = this.getParams();
        rawParams = {};
        if (index < this.numOfRequests)
          requests.push(axios.get(item, { params: rawParams, paramsSerializer }));
      });

      if (this.doFinalRequest) {
        // need to set a timeout because sometimes BE isn't ready to return calculated total headers.
        setTimeout(async () => {
          try {
            const result = await axios.get(this.finalRequestUrl, {
              params: rawParams,
              paramsSerializer
            });

            this.data = this.data.concat(result.data.tableInfo.data);

            result.data.tableInfo.data = this.data;

            this.handleFinished(result.data);
          } catch (err) {
            this.handleFail(err);
          }
        }, 500);
      } else {
        try {
          const result = await axios.all(requests);
          const canContinue = this.handleProgress(result);

          if (!canContinue) {
            result[0].data.tableInfo.data = this.data;
            this.handleFinished(result[0].data);
          }
        } catch (err) {
          this.handleFail(err);
        }
      }
    } else {
      if (shortUrl && this.nextLink) {
        rawDatapath = this.nextLink;
      } else if (shortUrl) {
        rawDatapath = baseUrl + "?short_url=" + shortUrl;
      } else {
        rawDatapath = this.nextLink || baseUrl;
        rawParams = this.getParams();
      }

      try {
        const result = await axios.get(rawDatapath, { params: rawParams, paramsSerializer });

        if (result.data.pagination === undefined) {
          this.handleFinished(result.data);
        } else if (!result.data.pagination.enabled) {
          this.handleFinished(result.data);
        } else {
          this.handleProgress(result.data);
        }
      } catch (err) {
        this.handleFail(err);
      }
    }
  }

  getParams() {
    const { query } = this.options;

    return {
      organization_id: query.organizationId,
      category_ids: query.category_ids,
      // location_id: query.locationId,
      // object_id: query.objectId,
      // region_id: query.regionId,
      date: query.date,
      start_date: query.start_date,
      end_date: query.end_date,
      search_start: query.search_start,
      search_end: query.search_end,

      year: query.year,
      month: query.month,
      day: query.day,

      shift_months: query.shift_months,
      period: query.period,
      period_days: query.period_days,
      period_type: query.period_type,

      filters: query.filters,
      chart_name: query.chart_name,

      category_type: query.category_type,
      category_type_id: query.category_type_id,

      exclude_offers: query.exclude_offers,
      exclude_intros: query.exclude_intros,
      include_returns: query.include_returns,
      count_no_shows: query.count_no_shows,
      show_expenses: query.show_expenses,
      include_cross_regional_visits: query.include_cross_regional_visits,

      dashboard_short_url: query.dashboard_short_url,
      short_url: query.dashboard_short_url
    };
  }

  getShortParams() {
    const { query } = this.options;

    return {
      organization_id: query.organizationId
    };
  }
}

function paramsSerializer(params) {
  const { filters } = params;
  const plainObj = omit(params, ["filters"]);

  return `${jQuery.param(plainObj)}&${jQuery.param({ filters })}`;
}
